import React from 'react';

const techStack = [
  'OpenAI', 'Claude', 'Gemini', 'Llama', 'Hugging Face',
  'LangChain', 'LangGraph', 'LangSmith', 'Langfuse', 'LlamaIndex', 'Pydantic AI', 'RAG',
  'Pinecone', 'Qdrant', 'Weaviate',
  'Whisper', 'Deepgram', 'ElevenLabs', 'Twilio',
  'Make.com',
  'Python', 'FastAPI', 'Node.js', 'TypeScript', 'React', 'Next.js', 'Tailwind',
  'Postgres', 'Supabase', 'Redis',
  'AWS', 'GCP', 'Vercel', 'Docker',
];

interface Service {
  number: string;
  title: string;
  description: string;
  tags: string[];
  icon: React.ReactNode;
}

const services: Service[] = [
  {
    number: '01',
    title: 'AI Agents & LLM Apps',
    description:
      'End-to-end development of autonomous AI agents that make decisions, use tools, and complete multi-step tasks without you lifting a finger. Built for real production workloads, not demos.',
    tags: ['LangGraph', 'OpenAI', 'Multi-agent', 'Tool use'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    number: '02',
    title: 'Chatbots & RAG Pipelines',
    description:
      'Custom chatbots that answer questions from your own documents, databases, and business data with accuracy and guardrails. If your team answers the same questions daily, a RAG chatbot can handle it.',
    tags: ['RAG', 'LangChain', 'Vector DBs', 'Guardrails'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Workflow Automation',
    description:
      'Replace repetitive manual work with intelligent automations that run 24/7. From data processing pipelines to document workflows, we build systems that eliminate the busywork and scale with you.',
    tags: ['Make.com', 'Python', 'APIs', 'Scheduling'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Conversational AI',
    description:
      'Intelligent conversational experiences that go far beyond scripted bots. We build AI assistants that understand context, remember the conversation, and give answers that actually help across web, app, or messaging platforms.',
    tags: ['LLMs', 'Context memory', 'Multi-turn', 'Integrations'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Voice AI',
    description:
      'Natural, real-time voice interfaces powered by the latest speech-to-text and text-to-speech models. From voice-enabled assistants to automated phone agents, we build voice AI that sounds human and works reliably.',
    tags: ['Speech-to-text', 'TTS', 'Voice agents', 'Real-time'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    ),
  },
  {
    number: '06',
    title: 'Application Development',
    description:
      'Full-stack applications built fast and right. Whether you need a dashboard, an internal tool, or a customer-facing product, we ship clean, scalable code that your team can maintain.',
    tags: ['React', 'FastAPI', 'Python', 'TypeScript'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
];

const Services: React.FC = () => {
  return (
    <section id="services" className="section-padding bg-neutral-50">
      <div className="container-max">
        <div className="max-w-2xl mb-16">
          <h2 className="text-4xl md:text-5xl font-semibold text-black mb-5 leading-[1.05] tracking-[-0.02em]">
            Services
          </h2>
          <p className="text-lg text-neutral-600 leading-relaxed">
            Practical AI solutions that go live, not slide decks about what's possible.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((service) => (
            <div
              key={service.number}
              className="bg-white rounded-2xl p-8 border border-neutral-200 flex flex-col h-full"
            >
              <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center text-neutral-700 mb-5">
                {service.icon}
              </div>
              <h3 className="text-lg font-semibold text-black mb-3 tracking-tight">
                {service.title}
              </h3>
              <p className="text-neutral-600 leading-relaxed text-[0.95rem]">{service.description}</p>
            </div>
          ))}
        </div>

        <div className="mt-16">
          <p className="text-sm text-neutral-500 mb-4">Tech we work with</p>
          <div className="flex flex-wrap gap-2">
            {techStack.map((tech) => (
              <span
                key={tech}
                className="inline-flex items-center px-3 py-1.5 rounded-full bg-white border border-neutral-200 text-neutral-700 text-xs font-medium"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
